import React, { useState, useMemo, useEffect } from 'react';
import {
  makeStyles, mergeClasses, tokens, Text, Checkbox, Input,
  Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell,
} from '@fluentui/react-components';
import { iconMap } from '../../../theme/icons';
import CellItem from '../CellItem';
import HandlesDialog from '../../dialogs/HandlesDialog';
import UniversalSelectionDialog from '../../dialogs/UniversalSelectionDialog';
import ItemViewDialog from '../../dialogs/ItemViewDialog';
import PanelSelectingDialog from '../../dialogs/PanelSelectingDialog';

// Paveiksliukai (Importuojame visus statinius assets, kurių gali prireikti)
import defaultDoorImage from './../../../assets/img/lmdp/B-0088_PR.jpg';
import handle1 from './../../../assets/img/handle_type/1.jpg';
import handle2 from './../../../assets/img/handle_type/2.jpg';
import handle3 from './../../../assets/img/handle_type/3.jpg';
import handle4 from './../../../assets/img/handle_type/4.jpg';
import handle5 from './../../../assets/img/handle_type/5.jpg';
import handle6 from './../../../assets/img/handle_type/6.jpg';
import handle7 from './../../../assets/img/handle_type/7.jpg';
import { API_HOST, API_BASE } from '../../../config.js';

const DoorFrontIcon = iconMap.door_front;
const MirrorIcon = iconMap.open_with;
const FillingIcon = iconMap.grid_view;
const ChevronRightIcon = iconMap.chevron_right;
const TouchAppIcon = iconMap.touch_app;
const RulerIcon = iconMap.straighten;
const SplitIcon = iconMap.border_all;
const MergeIcon = iconMap.merge;

export const HANDLE_IMAGES = { 0: handle1, 1: handle2, 2: handle3, 3: handle4, 4: handle5, 5: handle6, 6: handle7 };

const HANDLE_TYPE_NAMES = {
  0: 'Be rankenėlių',
  1: 'Be rankenėlių (užlaida 2.5cm)',
  2: 'Be rankenėlių (sumažintos 2.5cm)',
  3: 'Rankenėlės',
  4: 'Įleidžiamos',
  5: 'Nuo paspaudimo',
  6: 'Frezuotos',
};
export const getHandleTypeLabel = (handleType) => HANDLE_TYPE_NAMES[handleType] ?? 'Nepasirinkta';

// Durų sparno tipas (žr. doorColors — ta pati reikšmė, kokią naudoja
// enumeratePanels.js/calculatePrice.js gamybos brėžiniams ir vyrių
// skaičiavimui) — rodoma lentelės "Plokštė" stulpelyje, kad būtų aišku,
// KURIS fizinis durų lapas atitinka eilutę (žr. /admin/bugs pridėtą
// paveikslėlį — "Kairinės"/"Dešininės").
const DOOR_SWING_LABELS = {
  1: 'Kairinės', 2: 'Dešininės', 3: 'Dvigubos',
  4: 'Atverčiama (viršus)', 5: 'Atverčiama (apačia)', 6: 'Stalčius',
};

const useStyles = makeStyles({
  // Visas blokas perdizainintas minimalistiškai/moderniai (profesionalesnė
  // versija), BET struktūra (lentelė su #/Plokštė/Filingai/Rankenėlės, "Visiems
  // MDF" varnelė, Durelių aukštis kortelė) palikta identiška — keitėsi tik
  // vizualinis sluoksnis žemiau. CellItem.jsx NELIESTAS — jis bendras
  // komponentas, naudojamas daugybėje kitų dialogų/lentelių visame projekte.
  root: { padding: '6px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },

  // Reali Fluent <Table> vietoj laisvai plūduriuojančių kortelių — kiekviena
  // durelių grupė (fizinis durų lapas) yra VIENA eilutė, su # / Plokštė /
  // Filingai,Veidrodžiai / Rankenėlės stulpeliais. Be išorinio rėmelio/fono —
  // minimalistinis dizainas (žr. /admin/bugs "nuimk ta lenteles border...
  // panaikink sviesiai pilka background") — eilutės atskirtos tik plonu
  // "hairline" apatiniu kontūru, ne dėžute.
  tableWrap: { borderRadius: tokens.borderRadiusMedium, overflow: 'hidden' },
  // table-layout:fixed + % stulpeliai (NE fiksuoti px) — lentelė/panelis siauras
  // (~300px), tad fiksuotas px plotis kiekvienam iš 3 stulpelių (pvz. 140px×3)
  // tiesiog neišsitektų (žr. anksčiau: toks bandymas nustūmė "Rankenėlės"
  // stulpelį už matomos srities). calc() automatiškai dalina LIKUSĮ (po #
  // stulpelio) plotį lygiomis dalimis, nepriklausomai nuo konteinerio pločio.
  table: { tableLayout: 'fixed', width: '100%' },
  headerCell: {
    fontWeight: tokens.fontWeightSemibold, fontSize: '10px', color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase', letterSpacing: '0.04em',
    paddingBottom: tokens.spacingVerticalS, borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  // Kiekviena durų grupės eilutė atskirta plonia linija (ne dėžute) ir švelniai
  // paryškėja hover'inant — vien tam, kad akis lengviau sektų eilutę per 3
  // stulpelius, langeliai patys (CellItem) lieka pagrindinis click/hover
  // indikatorius.
  row: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    borderLeft: '3px solid transparent',
    transitionProperty: 'background-color, border-left-color', transitionDuration: tokens.durationFast,
    ':hover': { backgroundColor: tokens.colorSubtleBackgroundHover },
    ':last-child': { borderBottomStyle: 'none' },
  },
  // Durų grupė, šiuo metu pažymėta 3D vaizde (žr. selectedGroupId aukščiau) — tas pats
  // žalias/brand kontūras, koks jau naudojamas "pasirinkta" būsenai CellItem/SelectionBox
  // (cellItemSelected/boxActive), tik pritaikytas visai eilutei kairinio brūkšnio pavidalu,
  // kad neužgožtų pačių langelių vidinio turinio.
  rowSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    borderLeftColor: tokens.colorBrandStroke1,
  },
  idCell: { width: '44px', textAlign: 'center', verticalAlign: 'middle' },
  // VIENODAS plotis visoms 3 duomenų stulpeliams — be šito kiekvienas stulpelis
  // savaime platėtų/siaurėtų pagal savo paties turinį (pvz. ilgesnė antraštė
  // "Filingai, Veidrodžiai" vs trumpesnė "Plokštė"). calc() (ne fiksuotas px)
  // kartu su table-layout:fixed (žr. s.table aukščiau) — dalina LIKUSĮ (po 44px
  // # stulpelio) plotį per 3 lygiomis dalimis, visada tiksliai sutelpa,
  // nepriklausomai nuo panelio pločio.
  dataCell: { width: 'calc((100% - 44px) / 3)' },
  // Vienas kompaktiškas apvalus "badge" su grupės numeriu vietoj anksčiau
  // buvusio [pusjuodis tekstas + chevron] — chevron nieko neveikė (nebuvo
  // paspaudžiamas) ir skaitant lentelę atrodė kaip netyčinis vizualinis
  // triukšmas.
  groupBadge: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    minWidth: '24px', height: '22px', paddingLeft: '6px', paddingRight: '6px',
    borderRadius: tokens.borderRadiusCircular, backgroundColor: tokens.colorNeutralBackground3,
  },
  groupLabelText: { fontSize: '11px', fontWeight: tokens.fontWeightBold, color: tokens.colorNeutralForeground1 },

  // Kompaktiškas "langelio turinys" — mažas paveikslėlis viršuje + dvi
  // eilutės teksto apačioje (pilka etiketė, po ja pusjuodė reikšmė). Ne
  // SelectionBox (jis skirtas didesniems grid/dialogo mygtukams,
  // minHeight:140px būtų per didelis lentelės eilutei).
  cellStack: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: tokens.spacingVerticalXS },
  cellEmpty: { fontSize: '11px', color: tokens.colorNeutralForeground4, textAlign: 'center', display: 'block' },

  // "Nėra/Yra" segmentuotas jungiklis (vieno pilko "takelio" su pakeltu baltu
  // aktyviu segmentu stiliumi — žr. macOS/iOS segmented control), vietoj
  // anksčiau buvusių dviejų atskirai plūduriuojančių pilių su rėmeliu.
  segmented: {
    display: 'inline-flex', gap: '2px', padding: '2px',
    borderRadius: tokens.borderRadiusMedium, backgroundColor: tokens.colorNeutralBackground3,
  },
  segmentedOption: {
    paddingLeft: '10px', paddingRight: '10px', paddingTop: '3px', paddingBottom: '3px',
    borderRadius: tokens.borderRadiusSmall, cursor: 'pointer', textAlign: 'center',
    fontSize: '11px', fontWeight: tokens.fontWeightMedium, color: tokens.colorNeutralForeground3,
    transitionProperty: 'all', transitionDuration: '0.12s',
    ':hover': { color: tokens.colorNeutralForeground1 },
  },
  segmentedOptionActive: {
    backgroundColor: tokens.colorNeutralBackground1, color: tokens.colorNeutralForeground1,
    boxShadow: tokens.shadow2,
  },

  linkChip: {
    display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXS,
    paddingLeft: '10px', paddingRight: '10px', paddingTop: '3.2px', paddingBottom: '3.2px',
    borderRadius: tokens.borderRadiusCircular, border: `1px solid ${tokens.colorNeutralStroke2}`, cursor: 'pointer',
    backgroundColor: tokens.colorNeutralBackground1,
    transitionProperty: 'all', transitionDuration: '0.12s',
    ':hover': { borderColor: tokens.colorBrandStroke1, backgroundColor: tokens.colorBrandBackground2 },
  },
  linkChipMt: { marginTop: '6.4px' },
  linkChipText: { fontWeight: tokens.fontWeightMedium, color: tokens.colorNeutralForeground2 },
  linkChipTextActive: { fontWeight: tokens.fontWeightMedium, color: tokens.colorBrandForeground2 },
  linkChipChevron: { fontSize: '13px', color: tokens.colorNeutralForeground4 },

  applyAllCheckbox: { marginLeft: tokens.spacingHorizontalXS },
  applyAllLabel: { fontSize: '11px', color: tokens.colorNeutralForeground2 },

  // Be rėmelio/fono — žr. /admin/bugs "Nuimti border nuo sio bloko" (ši
  // korelė sąmoningai "neturi" dėžutės formos, panelio fonas ir
  // colorNeutralBackground1 yra ta pati balta spalva).
  doorHeightCard: { padding: tokens.spacingHorizontalS },
  doorHeightCheckboxLabel: {
    display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXS,
    fontSize: '12px', fontWeight: tokens.fontWeightMedium, color: tokens.colorNeutralForeground1,
  },
  doorHeightCheckboxIcon: { fontSize: '15px', color: tokens.colorNeutralForeground3 },
  doorHeightInputWrap: { marginTop: tokens.spacingVerticalS, width: '220px', display: 'block' },
  // Vienas sąrašas apjungia VISUS kambario baldus — kiekvienas gauna savo
  // uppercase/tracking antraštę (ta pati kalba, kaip lentelės headerCell),
  // rodomą tik kai baldų daugiau nei vienas (vienam baldui antraštė nereikalinga).
  doorHeightFurnitureGroups: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  doorHeightFurnitureGroup: { display: 'flex', flexDirection: 'column', gap: '2px' },
  doorHeightFurnitureLabel: {
    fontSize: '10px', fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase', letterSpacing: '0.04em',
    paddingLeft: tokens.spacingHorizontalS, marginBottom: '2px',
  },
  // Kiekvienam korpusui atskiras laukelis (žr. ClickUp "3.0 Išorė. Meniu -
  // 'Durelių aukštis'. Kiekvienam korpusui.") — anksčiau vienas laukelis
  // taikydavosi visam baldui iškart.
  doorHeightList: { display: 'flex', flexDirection: 'column', gap: '2px' },
  doorHeightRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalS,
    paddingTop: '4px', paddingBottom: '4px', paddingLeft: tokens.spacingHorizontalS, paddingRight: '2px',
    marginLeft: `-${tokens.spacingHorizontalS}`, borderRadius: tokens.borderRadiusSmall,
    transitionProperty: 'background-color', transitionDuration: tokens.durationFast,
    ':hover': { backgroundColor: tokens.colorSubtleBackgroundHover },
  },
  doorHeightRowLabel: { fontSize: '11px', color: tokens.colorNeutralForeground3, flexShrink: 0, fontWeight: tokens.fontWeightMedium },

  // "Nėra durelių" paaiškinamasis blokas (žr. ClickUp "3.0 Išorė. Meniu -
  // Tekstas, jei nėra durelių") — be rėmelio/fono, ta pati minimalistinė
  // kalba, kaip doorHeightCard. Kiekviena patarimo eilutė gauna vienodą
  // apvalų piktogramos "badge", kad trys eilutės skaitytųsi kaip vienas
  // tvarkingas sąrašas, ne atskiros pastraipos.
  emptyInfoCard: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, padding: tokens.spacingHorizontalS },
  emptyInfoRow: { display: 'flex', alignItems: 'flex-start', gap: tokens.spacingHorizontalS },
  emptyInfoIconBadge: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    width: '26px', height: '26px', borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground3, color: tokens.colorNeutralForeground2,
    marginTop: '1px',
  },
  emptyInfoText: { fontSize: '11px', lineHeight: 1.45, color: tokens.colorNeutralForeground2, paddingTop: '4px' },
});

const PlatesFillingsAndHandles = ({
  allCabinets,
  doorHandles, setDoorHandles,
  selectedShelf,
  mergedSections,
  customDoorHeightEnabled, setCustomDoorHeightEnabled,
  doorBottomHeight, setDoorBottomHeight,
  doorPlates, setDoorPlates,
  doorMirrors, setDoorMirrors,
  doorFillings, setDoorFillings,
  specificHandles, setSpecificHandles,
  doorGroupLabels,
  doorColors,
  carcassPlate,
}) => {
  const s = useStyles();
  const [activeDialog, setActiveDialog] = useState({ type: null, groupId: null, subType: null });
  const [fillingsApplyAll, setFillingsApplyAll] = useState(false);
  // "Žiūrėti" peržiūros dialogas — bendras visiems langelio tipams (plokštė,
  // veidrodis, filingas, rankenėlė), žr. ItemViewDialog imageUrl props.
  const [viewState, setViewState] = useState(null);

  // --- DATA PREP ---
  // mergedSections/doorPlates/doorHandles/etc. are the room-wide (not per-furniture-filtered)
  // state, so doorGroups here already spans every furniture piece — furnitureOrder just lets
  // the UI number them per-piece ("1.1", "1.2", "2.1", ...) instead of one flat, ungrouped list,
  // so it's clear which selections belong to which furniture piece in a multi-furniture room.
  // MUST read allCabinets (VISO kambario korpusai), NE cabinets prop — pastarasis ConfigPanel.jsx
  // gauna kaip `cabinets: activeCabinets` (žr. Layout.jsx configPanelProps), t.y. jau atfiltruotas
  // iki VIEN aktyvaus baldo; naudojant jį čia furnitureOrder tylomis niekada neviršydavo 1 įrašo,
  // net kai kambaryje realiai yra keli baldai (nerasta anksčiau, nes paveikė tik retai pasiekiamą
  // atsarginę etiketę, ne pagrindinę "1.1/2.1" numeraciją — ta ateina iš atskiro doorGroupLabels).
  const furnitureOrder = useMemo(() => {
    const order = [];
    (allCabinets || []).forEach(c => { if (c.furnitureId && !order.includes(c.furnitureId)) order.push(c.furnitureId); });
    return order;
  }, [allCabinets]);

  // doorBottomHeight yra VISO KAMBARIO laukas (setDoorBottomHeight rašo į bendrą
  // furnitureData.doorBottomHeight, ne į konkretaus baldo cabinets[]), tad
  // "Durelių aukštis" kortelė rodo VISUS kambario baldus vienoje vietoje — vartotojui
  // nereikia perjunginėti aktyvaus baldo, kad pakeistų kito baldo durelių aukštį (žr.
  // ClickUp "3.0 Išorė. Meniu - 'Durelių aukštis'. Kiekvienam korpusui.").
  // Grupuojame PAGAL BALDĄ, nes raktas BŪTINAI turi furnitureId prefiksą — vien
  // korpuso indeksas (0,1,2...) kiekvienam baldui prasideda iš naujo, tad be
  // prefikso dviejų baldų "korpusas 0" rašytų į TĄ PATĮ raktą ir tyliai perrašytų
  // vienas kitą (ta pati rizika, kaip anksčiau ištaisyta doorHandles — žr.
  // getHandleTypeForGroup komentarą aukščiau).
  const cabinetsByFurniture = useMemo(() => {
    const map = {};
    (allCabinets || []).forEach((c) => {
      if (!c.furnitureId) return;
      if (!map[c.furnitureId]) map[c.furnitureId] = [];
      map[c.furnitureId].push(c);
    });
    return map;
  }, [allCabinets]);
  const doorBottomHeightMap = (doorBottomHeight && typeof doorBottomHeight === 'object') ? doorBottomHeight : {};
  const setCabinetDoorHeight = (furnitureId, cabinetIdx, value) => {
    setDoorBottomHeight({ ...doorBottomHeightMap, [`${furnitureId}-${cabinetIdx}`]: value });
  };

  // "1.1, 1.2, 2.1, ..." labels for the group badges — computed once in Layout.jsx (which also
  // discards stale/orphaned merge entries whose sections no longer exist in the structure tree)
  // and shared with the 3D view (Builder3D) so a door's on-model number always matches its menu
  // row. A group only shows a card here if it also got a label — keeps orphaned entries (e.g.
  // left behind by a divider delete) from appearing as empty "phantom" groups in the menu.
  const groupLabels = doorGroupLabels || {};

  const doorGroups = useMemo(() => {
    const groups = {};
    (mergedSections || []).forEach(section => {
      if (!groupLabels[section.groupId]) return;
      if (!groups[section.groupId]) groups[section.groupId] = { id: section.groupId, furnitureId: section.furnitureId, sections: [] };
      groups[section.groupId].sections.push(section);
    });
    // Solo (never explicitly merged) doors — each still gets its own single-door "group" here,
    // self-referencing so getHandleTypeForGroup/getPlateForGroup (which just read
    // group.sections[0]) work the same way as for a real merged group. Matches the synthetic
    // `${furnitureId}-${cabinetIndex}-${path}` group id doorGroupLabels assigns them.
    Object.entries(doorColors || {}).forEach(([key, doorOption]) => {
      if (!doorOption || groups[key] || !groupLabels[key]) return;
      const dash1 = key.indexOf('-');
      const dash2 = key.indexOf('-', dash1 + 1);
      if (dash1 === -1 || dash2 === -1) return;
      const furnitureId = key.slice(0, dash1);
      const cabinetIndex = Number(key.slice(dash1 + 1, dash2));
      const sectionIndex = key.slice(dash2 + 1);
      groups[key] = {
        id: key, furnitureId,
        sections: [{ furnitureId, cabinetIndex, sectionIndex, targetCabinetIndex: cabinetIndex, targetSectionIndex: sectionIndex }],
      };
    });
    return Object.values(groups).sort((a, b) => {
      const fa = furnitureOrder.indexOf(a.furnitureId);
      const fb = furnitureOrder.indexOf(b.furnitureId);
      if (fa !== fb) return fa - fb;
      return String(a.id).localeCompare(String(b.id));
    });
  }, [mergedSections, furnitureOrder, groupLabels, doorColors]);

  // Dvigubos durys (žr. ClickUp "3.0 Išorė. Meniu - Dviguboms durims leidžiame
  // pasirinkti konkrečias plokštes/rankenėles/veidrodžius... T.y. kairinėms ir
  // dešininėms skirtingi pasirinkimai") — kairinis/dešininis lapas gauna SAVO,
  // nepriklausomą plokštės/veidrodžio/filingo/rankenėlės pasirinkimą, raktą
  // papildant "::L"/"::R" priesaga. Vienodurės/sujungtos grupės nepaliestos —
  // jos ir toliau naudoja paprastą group.id raktą be priesagos.
  const isGroupDouble = (group) => {
    const s0 = group.sections[0];
    if (!s0) return false;
    return doorColors?.[`${s0.furnitureId}-${s0.cabinetIndex}-${s0.sectionIndex}`] === 3;
  };
  const sideKey = (id, side) => (side ? `${id}::${side}` : id);
  const getGroupRowKeys = (group) => (
    isGroupDouble(group) ? ['L', 'R'].map((side) => ({ side, key: sideKey(group.id, side) })) : [{ side: null, key: group.id }]
  );

  // selectedShelf — paskutinis 3D vaizde paspaustas skydelis/durelės (Builder3D.jsx lokalus
  // state'as, mirroruojamas į Layout.jsx per onSelectedShelfChange, žr. Layout.jsx komentarą
  // ties jo deklaracija) — paverčiame į group.id, kad atitinkama eilutė čia būtų paryškinta
  // (vartotojo prašymas: "kai pazymiu dureles 3D vaizde, turi matytis kurios dureles pazymetos
  // menu"). Ta pati sujungtos grupės paieškos logika, kokia JAU naudojama Builder3D.jsx viduje
  // (žr. ten esamą selectedShelf → mergedSections atitikmens paiešką) — jei atitikmens
  // sujungtose grupėse nėra, krentame į tą patį sintetinį raktą, kokį naudoja pavienės durys
  // aukščiau (doorColors/groups[key]). Jei paspaustas elementas apskritai nėra durys (pvz.
  // vidinė lentyna), joks group.id nerandamas — tiesiog niekas neparyškinama, be klaidos.
  const selectedGroupId = useMemo(() => {
    if (!selectedShelf || selectedShelf.furnitureId == null || selectedShelf.cabinetIndex == null || selectedShelf.shelfIndex == null) return null;
    const merged = (mergedSections || []).find((s) =>
      s.furnitureId === selectedShelf.furnitureId && s.cabinetIndex === selectedShelf.cabinetIndex &&
      (s.sectionIndex === selectedShelf.shelfIndex || s.targetSectionIndex === selectedShelf.shelfIndex)
    );
    if (merged) return merged.groupId;
    return `${selectedShelf.furnitureId}-${selectedShelf.cabinetIndex}-${selectedShelf.shelfIndex}`;
  }, [selectedShelf, mergedSections]);

  // Durų grupė GALI rinktis kitokią plokštę nei karkaso (žr. žemiau Plokštė
  // langelio onClick — ClickUp "3.0 Išorė. Meniu - Action mygtukai": "Plokštė:
  // Viršuje 'Pasirinkti'"), tad šis effect'as nebeprivalo priverstinai
  // perrašyti VISŲ grupių — tik parenka karkaso plokštę kaip NUMATYTĄJĄ
  // reikšmę naujai atsiradusiai durų grupei (dar neturinčiai savo
  // doorPlates[groupId] įrašo). Jau turimas, nuo karkaso besiskiriantis
  // pasirinkimas NEBEPERRAŠOMAS vien dėl to, kad karkaso plokštė pasikeitė.
  useEffect(() => {
    if (!carcassPlate || !setDoorPlates) return;
    setDoorPlates((prev) => {
      let changed = false;
      const next = { ...prev };
      doorGroups.forEach((g) => {
        getGroupRowKeys(g).forEach(({ key }) => {
          if (!next[key]) { next[key] = carcassPlate; changed = true; }
        });
      });
      return changed ? next : prev;
    });
    // Filingai (MDF) ir veidrodžiai (LMDP) savitarpyje išsiskiria pagal plokštės tipą —
    // jei karkaso plokštės tipas pasikeitė, išvalome tą pusę, kuri nebeaktuali (ta pati
    // logika, kokia anksčiau buvo pačiame rankiniame onPlateSelect).
    if (carcassPlate.panelType === 1 && setDoorMirrors) {
      setDoorMirrors((prev) => {
        const next = { ...prev };
        let changed = false;
        doorGroups.forEach((g) => { getGroupRowKeys(g).forEach(({ key }) => { if (next[key]) { delete next[key]; changed = true; } }); });
        return changed ? next : prev;
      });
    } else if (carcassPlate.panelType === 0 && setDoorFillings) {
      setDoorFillings((prev) => {
        const next = { ...prev };
        let changed = false;
        doorGroups.forEach((g) => { getGroupRowKeys(g).forEach(({ key }) => { if (next[key]) { delete next[key]; changed = true; } }); });
        return changed ? next : prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carcassPlate, doorGroups.map((g) => g.id).join(',')]);

  // --- HELPERS ---
  // Numatytasis = karkaso plokštė, bet kiekviena durų grupė gali turėti savo,
  // nuo karkaso besiskiriantį pasirinkimą (doorPlates[groupId] — žr. useEffect
  // aukščiau ir Plokštė langelio onClick žemiau).
  const getPlateForGroup = (groupId) => doorPlates?.[groupId] || carcassPlate || null;

  // Keyed with furnitureId (not just cabinetIndex-sectionIndex) — cabinetIndex restarts at 0
  // for every furniture piece, so without furnitureId two different furniture pieces' cabinet-0
  // doors would silently overwrite each other's handle selection. Matches the door handle key
  // format already used for reading/writing doorHandles elsewhere (SingleCabinet3D.jsx).
  // `side` ('L'/'R'/undefined) — žr. sideKey aukščiau — leidžia dvigubų durų kairiniam/
  // dešininiam lapui turėti savo rankenėlės tipą.
  const getHandleTypeForGroup = (group, side) => {
    if (!group.sections[0]) return 0;
    const s = group.sections[0];
    const key = sideKey(`${s.furnitureId}-${s.cabinetIndex}-${s.sectionIndex}`, side);
    return doorHandles[key] || 0;
  };

  const getMirrorForGroup = (groupId) => doorMirrors?.[groupId] || { type: 'none', item: null };
  const getFillingForGroup = (groupId) => doorFillings?.[groupId] || { active: false, item: null };
  const getSpecificHandleForGroup = (groupId) => specificHandles?.[groupId] || null;

  const getPlateImage = (plateData) => {
    if (!plateData) return defaultDoorImage;
    const imageName = plateData.image_1 || plateData.image;
    if (!imageName) return defaultDoorImage;
    if (imageName.startsWith('http')) return imageName;
    const folder = plateData.panelType === 1 ? 'mdf' : 'lmdp';
    return `/${folder}/${imageName}`;
  };

  const getExternalImageUrl = (imageName) => {
      if(!imageName) return null;
      if(imageName.startsWith('http')) return imageName;
      return `${API_HOST}/uploads/${imageName}`;
  };

  const getHandleDescription = (handleType) => getHandleTypeLabel(handleType);

  // --- HANDLERS ---

  // RANKENĖLĖS
  const onHandleTypeSelect = (handleType) => {
    const { groupId, side } = activeDialog;
    if (groupId) {
        const group = doorGroups.find(g => g.id === groupId);
        if (group) {
            setDoorHandles(prev => {
                const newHandles = { ...prev };
                group.sections.forEach(sec => {
                    newHandles[sideKey(`${sec.furnitureId}-${sec.cabinetIndex}-${sec.sectionIndex}`, side)] = handleType;
                    newHandles[sideKey(`${sec.furnitureId}-${sec.targetCabinetIndex}-${sec.targetSectionIndex}`, side)] = handleType;
                });
                return newHandles;
            });
            if(handleType !== 3) {
                const selKey = sideKey(groupId, side);
                setSpecificHandles(prev => { const n = {...prev}; delete n[selKey]; return n; });
            }
        }
    }
    setActiveDialog({ type: null });
  };

  const onSpecificHandleSelect = (item) => {
      const { groupId, side } = activeDialog;
      if(groupId) {
          setSpecificHandles(prev => ({ ...prev, [sideKey(groupId, side)]: item }));
      }
      setActiveDialog({ type: null });
  };

  // PLOKŠTĖ (durų grupės savarankiškas pasirinkimas — žr. getPlateForGroup aukščiau)
  const onPlateItemSelect = (item) => {
      const { groupId, side } = activeDialog;
      if (groupId && item) {
          setDoorPlates(prev => ({ ...prev, [sideKey(groupId, side)]: item }));
      }
      setActiveDialog({ type: null });
  };

  // VEIDRODŽIAI — Nėra/Išorėje/Viduje pasirinkimas dabar gyvena PAČIAME "Pasirinkite
  // veidrodį" dialoge (žr. UniversalSelectionDialog placementOptions), ne atskira pilių
  // eilute lentelėje šalia jo (žr. /admin/bugs "Sie pasirinkimai turi buti veidrodzio
  // dialog lange"). Dialogas jau būna atidarytas, kai šis handler'is iškviečiamas — jis
  // TIK atnaujina būseną; "Nėra" papildomai uždaro dialogą, nes daugiau nėra ką rinktis.
  const handleMirrorTypeChange = (groupId, type) => {
      if (type === 'none') {
          setDoorMirrors(prev => ({ ...prev, [groupId]: { type: 'none', item: null } }));
          setActiveDialog({ type: null });
      } else {
          setDoorMirrors(prev => ({ ...prev, [groupId]: { type, item: prev[groupId]?.item } }));
      }
  };

  const onMirrorItemSelect = (item) => {
      const { groupId, side } = activeDialog;
      if(groupId) {
          const selKey = sideKey(groupId, side);
          const currentType = getMirrorForGroup(selKey).type;
          setDoorMirrors(prev => ({ ...prev, [selKey]: { type: currentType, item } }));
      }
      setActiveDialog({ type: null });
  };

  // FILINGAI — `key` čia jau PILNAS (iš renderRow gautas) pasirinkimo raktas,
  // dvigubų durų atveju jau su "::L"/"::R" priesaga (žr. selectionKey renderRow
  // viduje) — ne "grynas" group.id, tad activeDialog.groupId šiai dialogo rūšiai
  // laiko TIESIOGIAI šį pilną raktą (side lauko nereikia, nes jis jau įskaičiuotas).
  const handleFillingChange = (key, hasFilling) => {
      if (!hasFilling) {
          setDoorFillings(prev => ({ ...prev, [key]: { active: false, item: null } }));
      } else {
          setDoorFillings(prev => ({ ...prev, [key]: { active: true, item: prev[key]?.item } }));
          setActiveDialog({ type: 'filling_select', groupId: key });
      }
  };

  const onFillingItemSelect = (item) => {
      const { groupId, side } = activeDialog;
      if(fillingsApplyAll) {
          // "Visoms MDF durelėms" — kiekvienai grupei TIKRINAME/RAŠOME per jos pačios
          // eilutės raktą (getGroupRowKeys): dvigubų durų atveju abu lapai gali turėti
          // SKIRTINGĄ plokštę (vienas MDF, kitas ne), tad tai tikrinama atskirai kiekvienai
          // pusei, ne vieną kartą visai grupei.
          const newFillings = {};
          doorGroups.forEach(g => {
              getGroupRowKeys(g).forEach(({ key }) => {
                  const p = getPlateForGroup(key);
                  if(p && p.panelType === 1) {
                      newFillings[key] = { active: true, item };
                  }
              });
          });
          setDoorFillings(prev => ({ ...prev, ...newFillings }));
      } else {
          if(groupId) {
              setDoorFillings(prev => ({ ...prev, [sideKey(groupId, side)]: item }));
          }
      }
      setActiveDialog({ type: null });
  };


  // --- RENDER ---
  // Kol nėra nė vienos durų grupės, rodome trumpą paaiškinimą, kaip jas
  // sukurti/pasirinkti (žr. ClickUp "3.0 Išorė. Meniu - Tekstas, jei nėra
  // durelių" — anksčiau čia buvo kitas tekstas, kurį paprašyta panaikinti
  // visai; dabar prašoma vėl rodyti, bet su šiuo nauju tekstu).
  if (doorGroups.length === 0) {
    return (
      <div className={s.root}>
        <div className={s.emptyInfoCard}>
          <div className={s.emptyInfoRow}>
            <div className={s.emptyInfoIconBadge}>{TouchAppIcon && <TouchAppIcon fontSize={14} />}</div>
            <Text className={s.emptyInfoText}>
              Paspaudę ant baldo: Paspauskite ant skyriaus/durelių ir pasirinkite dureles ar išorinius stalčius.
            </Text>
          </div>
          <div className={s.emptyInfoRow}>
            <div className={s.emptyInfoIconBadge}>{SplitIcon && <SplitIcon fontSize={14} />}</div>
            <Text className={s.emptyInfoText}>
              <b>Durelių padalinimai:</b> Paspaudę ant durelių/skyriaus, rinkitės skaidyti horizontaliai/vertikaliai.
            </Text>
          </div>
          <div className={s.emptyInfoRow}>
            <div className={s.emptyInfoIconBadge}>{MergeIcon && <MergeIcon fontSize={14} />}</div>
            <Text className={s.emptyInfoText}>
              <b>Durelių apjungimai:</b> Paspauskite ant horizontalaus/vertikalaus atskyrimo ir jį ištrinkite.
            </Text>
          </div>
        </div>
      </div>
    );
  }

  const activeGroupHandleType = activeDialog.groupId
    ? getHandleTypeForGroup(doorGroups.find(g => g.id === activeDialog.groupId), activeDialog.side)
    : 0;

  const anyMdfGroup = doorGroups.some(g => getGroupRowKeys(g).some(({ key }) => getPlateForGroup(key)?.panelType === 1));

  return (
    <div className={s.root}>

      <div className={s.tableWrap}>
        <Table size="small" className={s.table}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell className={mergeClasses(s.idCell, s.headerCell)}>#</TableHeaderCell>
              <TableHeaderCell className={mergeClasses(s.headerCell, s.dataCell)}>Plokštė</TableHeaderCell>
              <TableHeaderCell className={mergeClasses(s.headerCell, s.dataCell)}>Filingai, Veidrodžiai</TableHeaderCell>
              <TableHeaderCell className={mergeClasses(s.headerCell, s.dataCell)}>Rankenėlės</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {doorGroups.flatMap((group) => {
              const label = groupLabels[group.id] || { text: '', furnitureNumber: 1 };
              const doorColorValue = doorColors?.[
                group.sections[0] ? `${group.sections[0].furnitureId}-${group.sections[0].cabinetIndex}-${group.sections[0].sectionIndex}` : ''
              ];
              const defaultSwingLabel = DOOR_SWING_LABELS[doorColorValue] || (furnitureOrder.length > 1 ? `Baldas ${label.furnitureNumber}` : 'Durelės');
              // Dvigubos durys — vienos durų grupės du lapai rodomi DVIEM atskiromis
              // eilutėmis (Kairinės/Dešininės), ne viena "Dvigubos" eilute — žr.
              // ClickUp "3.0 Išorė. Meniu - Dvigubas duris rodome kaip atskiras
              // sąraše". Numeracija ("#") palikta ta pati abiem — atskira
              // sub-numeracija niekur kitur sistemoje neegzistuoja. Nuo ClickUp
              // "Dviguboms durims leidžiame pasirinkti konkrečias plokštes/
              // rankenėles/veidrodžius" — kiekvienas lapas dabar turi SAVO
              // nepriklausomą pasirinkimą (žr. sideKey/getGroupRowKeys aukščiau),
              // tad plokštė/rankenėlė/veidrodis/filingas skaičiuojami VIDUJE
              // renderRow (su "side" parametru), ne čia, vienąkart visai grupei.
              const isDouble = isGroupDouble(group);

              const renderRow = (rowKey, swingLabel, side) => {
                const selectionKey = sideKey(group.id, side);
                const plate = getPlateForGroup(selectionKey);
                const handleType = getHandleTypeForGroup(group, side);
                const mirror = getMirrorForGroup(selectionKey);
                const filling = getFillingForGroup(selectionKey);
                const specificHandle = getSpecificHandleForGroup(selectionKey);

                const plateImageSrc = getPlateImage(plate);
                let handleImageSrc = HANDLE_IMAGES[handleType] || handle1;
                if (handleType === 3 && specificHandle) {
                  const img = specificHandle.image_1 || specificHandle.image_2;
                  if (img) handleImageSrc = getExternalImageUrl(img);
                }

                // Filingo katalogo "name" jau turi formą "Pavadinimas (dydis)"
                // (pvz. "Classic 1 (2x1)") — skaidome į pusjuodį pavadinimą ir
                // paprastą dydžio priedą, vietoj anksčiau rodyto SKU kodo (žr.
                // ClickUp "3.0 Išorė. Meniu - Durelių lentelė": "Nereikia išvesti
                // filingų skaičiaus... Galim rašyti bold 'Modern 2' ir ne bold (2x1)").
                const fillingNameMatch = filling.item?.name?.match(/^(.*?)\s*(\([^)]*\))\s*$/);
                const fillingBase = fillingNameMatch ? fillingNameMatch[1] : filling.item?.name;
                const fillingSuffix = fillingNameMatch ? fillingNameMatch[2] : '';

                return (
                  <TableRow key={rowKey} className={mergeClasses(s.row, group.id === selectedGroupId && s.rowSelected)}>
                    <TableCell className={s.idCell}>
                      <div className={s.groupBadge}>
                        <Text className={s.groupLabelText}>{label.text}</Text>
                      </div>
                    </TableCell>

                    {/* --- PLOKŠTĖ --- */}
                    <TableCell>
                      <CellItem
                        variant="list"
                        image={plate ? plateImageSrc : null}
                        icon={DoorFrontIcon} isIcon={!plate}
                        title={swingLabel}
                        subTitle={plate ? plate.code : 'Nepasirinkta'}
                        onClick={() => setActiveDialog({ type: 'plate_select', groupId: group.id, side })}
                        onView={() => setViewState({ item: plate, imageUrl: plateImageSrc, itemType: plate?.panelType === 1 ? 'mdf' : 'lmdp' })}
                        viewDisabled={!plate}
                      />
                    </TableCell>

                    {/* --- FILINGAI / VEIDRODŽIAI (tarpusavyje išskiriantys, pagal plokštės tipą) --- */}
                    <TableCell>
                      {!plate && <Text className={s.cellEmpty}>—</Text>}

                      {plate && plate.panelType === 0 && (
                        <CellItem
                          variant="list"
                          image={mirror.type !== 'none' && mirror.item ? getExternalImageUrl(mirror.item.image_1 || mirror.item.image_2) : null}
                          icon={MirrorIcon} isIcon={mirror.type === 'none' || !mirror.item}
                          title={mirror.type === 'none' ? 'Veidrodis' : (mirror.type === 'outside' ? 'Išorėje' : 'Viduje')}
                          subTitle={mirror.type === 'none' ? 'Nėra' : (mirror.item ? (mirror.item.code || mirror.item.name) : 'Pasirinkti')}
                          onClick={() => setActiveDialog({ type: 'mirror_select', groupId: group.id, side })}
                          onView={() => setViewState({ item: mirror.item, imageUrl: getExternalImageUrl(mirror.item?.image_1 || mirror.item?.image_2), itemType: 'mirror' })}
                          viewDisabled={mirror.type === 'none' || !mirror.item}
                        />
                      )}

                      {plate && plate.panelType === 1 && (
                        <div className={s.cellStack}>
                          <div className={s.segmented}>
                            {[{ v: false, l: 'Nėra' }, { v: true, l: 'Yra' }].map(({ v, l }) => (
                              <div
                                key={String(v)}
                                onClick={() => handleFillingChange(selectionKey, v)}
                                className={mergeClasses(s.segmentedOption, filling.active === v && s.segmentedOptionActive)}
                              >{l}</div>
                            ))}
                          </div>
                          {filling.active && (
                            <CellItem
                              variant="list"
                              image={filling.item ? getExternalImageUrl(filling.item.image_1 || filling.item.image_2) : null}
                              icon={FillingIcon} isIcon={!filling.item}
                              title={filling.item ? fillingSuffix : 'Filingas'}
                              subTitle={filling.item ? fillingBase : 'Pasirinkti'}
                              onClick={() => setActiveDialog({ type: 'filling_select', groupId: group.id, side })}
                              onView={() => setViewState({ item: filling.item, imageUrl: getExternalImageUrl(filling.item?.image_1 || filling.item?.image_2), itemType: 'filling' })}
                              viewDisabled={!filling.item}
                            />
                          )}
                        </div>
                      )}
                    </TableCell>

                    {/* --- RANKENĖLĖS --- */}
                    <TableCell>
                      <div className={s.cellStack}>
                        <CellItem
                          variant="list"
                          image={handleImageSrc}
                          title="Rankenėlės"
                          subTitle={handleType === 3 && specificHandle ? specificHandle.code : getHandleDescription(handleType)}
                          onClick={() => setActiveDialog({ type: 'handle_type', groupId: group.id, side })}
                          onView={() => setViewState({
                            item: handleType === 3 && specificHandle ? specificHandle : { name: getHandleDescription(handleType), code: '' },
                            imageUrl: handleImageSrc,
                            // Tikram katalogo elementui (handleType===3) naudojame pilną
                            // rankenėlių lauko sąrašą; bendriniams tipams (Be rankenėlių ir
                            // pan., neturintiems katalogo duomenų) itemType nenurodomas —
                            // ItemViewDialog atsargiai grąžta tik prie Pavadinimo, atitinkančio
                            // sintetinio elemento .name lauką.
                            itemType: handleType === 3 && specificHandle ? 'handle' : undefined,
                          })}
                          viewDisabled={handleType === 3 && !specificHandle}
                        />
                        {handleType === 3 && (
                          <div
                            onClick={() => setActiveDialog({ type: 'specific_handle_select', groupId: group.id, side })}
                            className={s.linkChip}
                          >
                            <Text size={200} className={specificHandle ? s.linkChipTextActive : s.linkChipText}>
                              {specificHandle ? specificHandle.code || 'Pasirinkta' : 'Modelis iš katalogo'}
                            </Text>
                            <ChevronRightIcon className={s.linkChipChevron} />
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              };

              return isDouble
                ? [renderRow(`${group.id}-L`, 'Kairinės', 'L'), renderRow(`${group.id}-R`, 'Dešininės', 'R')]
                : [renderRow(group.id, defaultSwingLabel, null)];
            })}
          </TableBody>
        </Table>
      </div>

      {/* "Visiems MDF" — bendras, ne kiekvienai eilutei atskirai (anksčiau tas
          pats varnelės mygtukas kartodavosi kiekvienoje MDF eilutėje). */}
      {anyMdfGroup && (
        <Checkbox
          className={s.applyAllCheckbox}
          checked={fillingsApplyAll}
          onChange={(e, data) => setFillingsApplyAll(data.checked)}
          label={<Text className={s.applyAllLabel}>Pasirinktą filingą taikyti visoms MDF durelėms</Text>}
        />
      )}

      {/* Durelių aukštis — VISIEMS kambario baldams vienoje vietoje, kiekvienam
          korpusui atskirai (žr. ClickUp "3.0 Išorė. Meniu - 'Durelių aukštis'.
          Kiekvienam korpusui."). doorBottomHeight — objektas
          {`${furnitureId}-${cabinetIdx}`: cm}, žr. setCabinetDoorHeight aukščiau. */}
      <div className={s.doorHeightCard}>
        <Checkbox
          checked={customDoorHeightEnabled}
          onChange={(e, data) => setCustomDoorHeightEnabled(data.checked)}
          label={
            <span className={s.doorHeightCheckboxLabel}>
              {RulerIcon && <RulerIcon className={s.doorHeightCheckboxIcon} />}
              Nurodyti durelių aukštį nuo grindų
            </span>
          }
        />
        {customDoorHeightEnabled && (
          <div className={s.doorHeightInputWrap}>
            <div className={s.doorHeightFurnitureGroups}>
              {furnitureOrder.map((fid, fIdx) => {
                const furnitureCabinets = cabinetsByFurniture[fid] || [];
                if (furnitureCabinets.length === 0) return null;
                return (
                  <div key={fid} className={s.doorHeightFurnitureGroup}>
                    {furnitureOrder.length > 1 && (
                      <Text className={s.doorHeightFurnitureLabel}>Baldas {fIdx + 1}</Text>
                    )}
                    <div className={s.doorHeightList}>
                      {furnitureCabinets.map((_, idx) => (
                        <div key={idx} className={s.doorHeightRow}>
                          <Text className={s.doorHeightRowLabel}>
                            {furnitureCabinets.length > 1 ? `Korpusas ${idx + 1}` : 'Aukštis nuo grindų (cm)'}
                          </Text>
                          <Input
                            type="number"
                            size="small"
                            value={String(doorBottomHeightMap[`${fid}-${idx}`] ?? '')}
                            onChange={(e, data) => setCabinetDoorHeight(fid, idx, Number(data.value))}
                            min={0}
                            max={200}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* DIALOGS */}
      <PanelSelectingDialog
        open={activeDialog.type === 'plate_select'}
        onClose={() => setActiveDialog({ type: null })}
        onItemSelect={onPlateItemSelect}
        currentPlate={activeDialog.groupId ? getPlateForGroup(sideKey(activeDialog.groupId, activeDialog.side)) : null}
      />
      <HandlesDialog open={activeDialog.type === 'handle_type'} onClose={() => setActiveDialog({ type: null })} onHandleSelect={onHandleTypeSelect} currentHandle={activeGroupHandleType} />
      <UniversalSelectionDialog
        open={activeDialog.type === 'mirror_select'}
        onClose={() => setActiveDialog({ type: null })}
        onSelect={onMirrorItemSelect}
        title="Pasirinkite veidrodį"
        apiUrl={`${API_BASE}/mirrors`}
        type="mirror"
        placementOptions={[{ value: 'none', label: 'Nėra' }, { value: 'outside', label: 'Išorėje' }, { value: 'inside', label: 'Viduje' }]}
        placementValue={activeDialog.groupId ? getMirrorForGroup(sideKey(activeDialog.groupId, activeDialog.side)).type : 'none'}
        onPlacementChange={(v) => handleMirrorTypeChange(sideKey(activeDialog.groupId, activeDialog.side), v)}
      />
      <UniversalSelectionDialog open={activeDialog.type === 'filling_select'} onClose={() => setActiveDialog({ type: null })} onSelect={onFillingItemSelect} title="Pasirinkite filingą" apiUrl={`${API_BASE}/fillings`} type="filling" />
      <UniversalSelectionDialog open={activeDialog.type === 'specific_handle_select'} onClose={() => setActiveDialog({ type: null })} onSelect={onSpecificHandleSelect} title="Pasirinkite rankenėlę" apiUrl={`${API_BASE}/handles`} type="handle" />
      <ItemViewDialog open={!!viewState} onClose={() => setViewState(null)} item={viewState?.item} imageUrl={viewState?.imageUrl} itemType={viewState?.itemType} />

    </div>
  );
};

export default PlatesFillingsAndHandles;
